import { InputLabel } from '@components/ui';
import { Grid, InputAdornment, makeStyles, Slider, TextField, TextFieldProps, Theme } from '@material-ui/core';
import { grey } from '@material-ui/core/colors';
import { debounce } from 'lodash';
import { memo, useCallback, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useCurrency } from '../hooks/useCurrency';
import { useDidUpdateEffect } from '../hooks/useDidUpdateEffect';
import { DEFAULT_CURRENCY, MonetaryAmountStrict, SUPPORTED_CURRENCIES } from '../modules/currency';
import { deserializeValue, serializeValue } from '../modules/filter-helper';

import AmountInput from './AmountInput';

type SliderInputProps = {
  name: string;
  label: string;
  step: number;
  min: number;
  max: number;
  marginTop?: boolean;
};

const useStyles = makeStyles<Theme, { marginTop: boolean }>((theme) => ({
  label: {
    display: 'block',
    marginBottom: theme.spacing(-0.5),
    marginTop: ({ marginTop }) => (marginTop ? 8 : 0),
  },
  textField: {
    marginTop: theme.spacing(-0.5),
    marginBottom: theme.spacing(2),
  },
}));

/**
 * react-number-format hands partial input over verbatim, so a lone "-" arrives as "-" and parseInt
 * turns it into NaN. That NaN survives the debounced write into form state — lodash isNumber(NaN)
 * is true — and serialises to `{"amount": null, "currency": "EUR"}` in the request. Returning
 * undefined keeps the partial input uncommitted until it parses. Only the price change filter has
 * a negative range, where "-" is the opening keystroke of normal use.
 */
const parseAmount = (value: string): number | '' | undefined => {
  if (value === '') return '';
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const CurrencySliderInput = ({ name, label, step, min, max, marginTop = false }: SliderInputProps) => {
  const { t } = useTranslation();
  const classes = useStyles({ marginTop });
  const { currency, convert } = useCurrency();
  const minLocalized = convert(min, { from: DEFAULT_CURRENCY.code });
  const maxLocalized = convert(max, { from: DEFAULT_CURRENCY.code });
  const stepLocalized = convert(step, { from: DEFAULT_CURRENCY.code });
  const { setValue: setFormValue, getValues: getFormValue, control } = useFormContext();
  const [value, setValue] = useState<[number | '', number | '']>(
    deserializeValue(getFormValue(name), (v, c) => convert(v, { from: c })),
  );
  const formValue = useWatch({ name, control }) as [MonetaryAmountStrict, MonetaryAmountStrict];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setFormValueDebounced = useCallback(debounce(setFormValue, 400), [setFormValue]);

  // populate internal state updates
  useDidUpdateEffect(() => {
    setFormValueDebounced(name, serializeValue(value, currency), { shouldTouch: true });

    return () => {
      setFormValueDebounced.cancel();
    };
  }, [value, name, setFormValueDebounced]);

  // sync external state updates
  useDidUpdateEffect(() => {
    setValue(deserializeValue(formValue, (v, c) => convert(v, { from: c })));
  }, [setValue, formValue, convert]);

  return (
    <div>
      <InputLabel
        style={{
          marginBlockEnd: 2,
          fontWeight: 'normal',
          fontSize: 12,
          color: grey[600],
        }}
      >
        {t(label as any)}
      </InputLabel>
      <Slider
        value={[value[0] === '' ? minLocalized : value[0], value[1] === '' ? maxLocalized : value[1]]}
        min={minLocalized}
        max={maxLocalized}
        step={stepLocalized}
        aria-labelledby={name}
        onChange={(_, changeValue) => {
          setValue(changeValue as [number, number]);
        }}
        color="secondary"
      />
      <Grid container spacing={1}>
        <Grid item xs={6}>
          <AmountInput<TextFieldProps>
            className={classes.textField}
            customInput={TextField}
            inputProps={{
              step: stepLocalized,
              min: minLocalized,
              max: maxLocalized,
              'aria-labelledby': name,
            }}
            InputProps={{
              endAdornment: <InputAdornment position="end">{SUPPORTED_CURRENCIES[currency].symbol}</InputAdornment>,
            }}
            margin="dense"
            value={value[0]}
            variant="outlined"
            emptyInputAllowed
            onValueChange={({ value: unformattedValue }) => {
              const parsed = parseAmount(unformattedValue);
              if (parsed === undefined) return;
              setValue((prev) => [parsed, prev[1]]);
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <AmountInput<TextFieldProps>
            className={classes.textField}
            customInput={TextField}
            inputProps={{
              step: stepLocalized,
              min: minLocalized,
              max: maxLocalized,
              'aria-labelledby': name,
            }}
            InputProps={{
              endAdornment: <InputAdornment position="end">{SUPPORTED_CURRENCIES[currency].symbol}</InputAdornment>,
            }}
            margin="dense"
            variant="outlined"
            value={value[1]}
            emptyInputAllowed
            onValueChange={({ value: unformattedValue }) => {
              const parsed = parseAmount(unformattedValue);
              if (parsed === undefined) return;
              setValue((prev) => [prev[0], parsed]);
            }}
          />
        </Grid>
      </Grid>
    </div>
  );
};

export default memo(CurrencySliderInput);
